import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ModuleGuard from "@/components/ModuleGuard";
import PermissionGuard from "@/components/PermissionGuard";
import { SubscriptionProvider, TrialBanner } from "@/components/SubscriptionGuard";
import DisableInspect from "@/components/DisableInspect";

// ⚡ LOADING COMPONENT - Leve e rápido
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-gray-500 text-sm">Carregando...</p>
    </div>
  </div>
);

// Retry wrapper para lazy imports - resolve ChunkLoadError em produção
function lazyWithRetry(importFn) {
  return lazy(() =>
    importFn().catch((err) => {
      // Se chunk falhou, tentar recarregar 1x
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // Nunca resolve, página recarrega
      }
      sessionStorage.removeItem('chunk_reload');
      throw err; // Se já tentou reload, propagar erro
    })
  );
}

// Limpar flag de reload quando carregou com sucesso
if (sessionStorage.getItem('chunk_reload')) {
  sessionStorage.removeItem('chunk_reload');
}

// ⚡ LAZY LOADING - Carrega apenas quando necessário
// Páginas principais (carregamento imediato)
import Home from "@/pages/Home";
import LandingPage from "@/pages/LandingPage";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import Contato from "@/pages/Contato";

// Páginas pesadas (lazy loading com retry automático)
const PDV = lazyWithRetry(() => import("@/pages/PDV"));
const Totem = lazyWithRetry(() => import("@/pages/Totem"));
const SalesPage = lazyWithRetry(() => import("@/pages/SalesPage"));

// Admin pages (lazy)
const AriaAssistant = lazyWithRetry(() => import("@/pages/admin/AriaAssistant"));
const InstagramGiveaway = lazyWithRetry(() => import("@/pages/admin/InstagramGiveaway"));
const SecurityCameras = lazyWithRetry(() => import("@/pages/admin/SecurityCameras"));
const AdminDashboard = lazyWithRetry(() => import("@/pages/admin/DashboardNew"));
const AdminProducts = lazyWithRetry(() => import("@/pages/admin/Products"));
const AdminStock = lazyWithRetry(() => import("@/pages/admin/StockNew"));
const AdminFinancial = lazyWithRetry(() => import("@/pages/admin/Financial"));
const FinancialDashboardNew = lazyWithRetry(() => import("@/pages/admin/FinancialDashboardNew"));
const FinancialTransactions = lazyWithRetry(() => import("@/pages/admin/FinancialTransactions"));
const FinancialDRE = lazyWithRetry(() => import("@/pages/admin/FinancialDRE"));
const RDS = lazyWithRetry(() => import("@/pages/admin/RDS"));
const CashFlow = lazyWithRetry(() => import("@/pages/admin/CashFlow"));
const FinancialReports = lazyWithRetry(() => import("@/pages/admin/FinancialReports"));
const SalesReport = lazyWithRetry(() => import("@/pages/admin/SalesReport"));
const Commissions = lazyWithRetry(() => import("@/pages/admin/Commissions"));
const AdminSales = lazyWithRetry(() => import("@/pages/admin/Sales"));
const AdminVendas = lazyWithRetry(() => import("@/pages/admin/Vendas"));
const AdminFiscal = lazyWithRetry(() => import("@/pages/admin/Fiscal"));
const NFCeXMLs = lazyWithRetry(() => import("@/pages/admin/NFCeXMLs"));
const AdminSettings = lazyWithRetry(() => import("@/pages/admin/Settings"));
const Devices = lazyWithRetry(() => import("@/pages/admin/Devices"));
const DeviceSelector = lazyWithRetry(() => import("@/pages/DeviceSelector"));
const AdminRelatorios = lazyWithRetry(() => import("@/pages/admin/Relatorios"));
const AdminCollaborators = lazyWithRetry(() => import("@/pages/admin/Collaborators"));
const AdminRoles = lazyWithRetry(() => import("@/pages/admin/RolesPermissions"));
const AdminTotemSettings = lazyWithRetry(() => import("@/pages/admin/TotemSettings"));
const AdminCashRegister = lazyWithRetry(() => import("@/pages/admin/CashRegister"));
const Suppliers = lazyWithRetry(() => import("@/pages/admin/Suppliers"));
const ExpenseCategories = lazyWithRetry(() => import("@/pages/admin/ExpenseCategories"));
const Categories = lazyWithRetry(() => import("@/pages/admin/Categories"));
const AccountsPayable = lazyWithRetry(() => import("@/pages/admin/AccountsPayable"));
const FinancialDashboard = lazyWithRetry(() => import("@/pages/admin/FinancialDashboard"));
const AccountsPayableNew = lazyWithRetry(() => import("@/pages/admin/AccountsPayableNew"));
const AccountsPaid = lazyWithRetry(() => import("@/pages/admin/AccountsPaid"));
const FinancialAccounts = lazyWithRetry(() => import("@/pages/admin/FinancialAccounts"));
const AccountsReceivable = lazyWithRetry(() => import("@/pages/admin/AccountsReceivable"));
const BalanceSheet = lazyWithRetry(() => import("@/pages/admin/BalanceSheet"));
const PayrollEmployees = lazyWithRetry(() => import("@/pages/admin/PayrollEmployees"));
const Payroll = lazyWithRetry(() => import("@/pages/admin/Payroll"));
const RecurringPayments = lazyWithRetry(() => import("@/pages/admin/RecurringPayments"));
const FinancialProjection = lazyWithRetry(() => import("@/pages/admin/FinancialProjection"));
const FinancialNotifications = lazyWithRetry(() => import("@/pages/admin/FinancialNotifications"));
const WhatsAppBot = lazyWithRetry(() => import("@/pages/admin/WhatsAppBot"));
const BudgetGoals = lazyWithRetry(() => import("@/pages/admin/BudgetGoals"));
const CostCenters = lazyWithRetry(() => import("@/pages/admin/CostCenters"));
const TaxReports = lazyWithRetry(() => import("@/pages/admin/TaxReports"));
const Delinquency = lazyWithRetry(() => import("@/pages/admin/Delinquency"));
const AuditLogs = lazyWithRetry(() => import("@/pages/admin/AuditLogs"));
const AdvancedDashboards = lazyWithRetry(() => import("@/pages/admin/AdvancedDashboards"));
const MercadoPagoTEF = lazyWithRetry(() => import("@/pages/admin/MercadoPagoTEF"));
const StockMovementReport = lazyWithRetry(() => import("@/pages/admin/StockMovementReport"));
const Welcome = lazyWithRetry(() => import("@/pages/admin/Welcome"));
const WebhookMonitor = lazyWithRetry(() => import("@/pages/admin/WebhookMonitor"));
const KitchenLabels = lazyWithRetry(() => import("@/pages/admin/kitchen-labels"));
const PrintAgent = lazyWithRetry(() => import("@/pages/admin/PrintAgent"));
const RecipeManagement = lazyWithRetry(() => import("@/pages/admin/RecipeManagement"));
const Producao = lazyWithRetry(() => import("@/pages/admin/Producao"));
const KitchenDisplay = lazyWithRetry(() => import("@/pages/admin/KitchenDisplay"));
const NFeImportPage = lazyWithRetry(() => import("@/pages/admin/NFeImportPage"));
const Suporte = lazyWithRetry(() => import("@/pages/admin/Suporte"));
const ClientsPage = lazyWithRetry(() => import("@/pages/admin/ClientsPage"));
const InternalConsumptionReport = lazyWithRetry(() => import("@/pages/admin/InternalConsumptionReport"));
const Comanda = lazyWithRetry(() => import("@/pages/admin/Comanda"));

// Delivery (admin + público)
const AdminDelivery = lazyWithRetry(() => import("@/pages/admin/Delivery"));
const AdminDeliveryOrders = lazyWithRetry(() => import("@/pages/admin/DeliveryOrders"));
const PublicDelivery = lazyWithRetry(() => import("@/pages/delivery/PublicDelivery"));
const TableMenu = lazyWithRetry(() => import("@/pages/TableMenu"));
const TableQRCodes = lazyWithRetry(() => import("@/pages/admin/TableQRCodes"));

// Hotel Module (lazy)
const HotelModule = lazyWithRetry(() => import("@/pages/hotel"));
const BookingEngine = lazyWithRetry(() => import("@/pages/hotel/BookingEngine"));

// Auth pages (lazy)
const ForgotPassword = lazyWithRetry(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("@/pages/ResetPassword"));
const Register = lazyWithRetry(() => import("@/pages/Register"));
const RegisterNew = lazyWithRetry(() => import("@/pages/RegisterNew"));
const ClearCache = lazyWithRetry(() => import("@/pages/ClearCache"));
const SubscriptionPayment = lazyWithRetry(() => import("@/pages/SubscriptionPayment"));

// Onboarding pages (lazy)
const ChoosePath = lazyWithRetry(() => import("@/pages/onboarding/ChoosePath"));
const CreateCompany = lazyWithRetry(() => import("@/pages/onboarding/CreateCompany"));
const JoinCompany = lazyWithRetry(() => import("@/pages/onboarding/JoinCompany"));
const PendingApproval = lazyWithRetry(() => import("@/pages/onboarding/PendingApproval"));

// SuperAdmin pages (lazy)
const SuperAdminLogin = lazyWithRetry(() => import("@/pages/superadmin/SuperAdminLogin"));
const SuperAdminDashboard = lazyWithRetry(() => import("@/pages/superadmin/SuperAdminDashboard"));
const SuperAdminSecurity = lazyWithRetry(() => import("@/pages/superadmin/Security"));
const CompanyModulesManager = lazyWithRetry(() => import("@/pages/superadmin/CompanyModules"));
const DataManagement = lazyWithRetry(() => import("@/pages/superadmin/DataManagement"));
const PaymentTerminals = lazyWithRetry(() => import("@/pages/superadmin/PaymentTerminals"));
const CompanyPaymentConfig = lazyWithRetry(() => import("@/pages/superadmin/CompanyPaymentConfig"));
const SuperAdminSubscriptions = lazyWithRetry(() => import("@/pages/admin/SuperAdminSubscriptions"));
const SuperAdminMessages = lazyWithRetry(() => import("@/pages/superadmin/SuperAdminMessages"));
const AutoBackups = lazyWithRetry(() => import("@/pages/superadmin/AutoBackups"));

function AppContent() {
  return (
    <>
      <TrialBanner />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/comprar" element={<SalesPage />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/suporte" element={<Contato />} />
          <Route path="/clear-cache" element={<ClearCache />} />
          
          {/* Super Admin Routes */}
          <Route path="/superadmin" element={<Navigate to="/superadmin/login" replace />} />
          <Route path="/superadmin/login" element={<SuperAdminLogin />} />
          <Route path="/superadmin/dashboard" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/security" element={<SuperAdminSecurity />} />
          <Route path="/superadmin/auto-backups" element={<AutoBackups />} />
          <Route path="/superadmin/company-modules" element={<CompanyModulesManager />} />
          <Route path="/superadmin/data-management" element={<DataManagement />} />
          <Route path="/superadmin/payment-terminals" element={<PaymentTerminals />} />
          <Route path="/superadmin/company-payment/:companyId" element={<CompanyPaymentConfig />} />
          <Route path="/superadmin/subscriptions" element={<SuperAdminSubscriptions />} />
          <Route path="/superadmin/messages" element={<SuperAdminMessages />} />
          
          {/* Subscription Routes */}
          <Route path="/subscription/payment" element={<SubscriptionPayment />} />
          
          {/* Onboarding Routes */}
          <Route path="/register-new" element={<RegisterNew />} />
          <Route path="/onboarding/choose" element={<ChoosePath />} />
          <Route path="/onboarding/create-company" element={<CreateCompany />} />
          <Route path="/onboarding/join-company" element={<JoinCompany />} />
          <Route path="/onboarding/pending-approval" element={<PendingApproval />} />
          
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/auth-callback" element={<AuthCallback />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<Welcome />} />
          <Route path="/admin/welcome" element={<Welcome />} />
          <Route path="/admin/dashboard" element={<PermissionGuard permission="view_dashboard"><AdminDashboard /></PermissionGuard>} />
          <Route path="/admin/totem" element={<ModuleGuard module="totem"><Totem /></ModuleGuard>} />
          <Route path="/admin/pdv" element={<ModuleGuard module="pdv"><PDV /></ModuleGuard>} />
          <Route path="/admin/products" element={<ModuleGuard module="products"><PermissionGuard permission="view_products"><AdminProducts /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/categories" element={<ModuleGuard module="products"><PermissionGuard permission="manage_products"><Categories /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/stock" element={<ModuleGuard module="stock"><PermissionGuard permission="view_stock"><AdminStock /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><FinancialDashboardNew /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial-old" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><AdminFinancial /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial/transactions" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><FinancialTransactions /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial/accounts-payable" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><AccountsPayableNew /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial/accounts-paid" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><AccountsPaid /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial/whatsapp-bot" element={<ModuleGuard module="whatsapp_bot"><WhatsAppBot /></ModuleGuard>} />
          <Route path="/admin/whatsapp-bot" element={<ModuleGuard module="whatsapp_bot"><WhatsAppBot /></ModuleGuard>} />
          <Route path="/admin/financial/accounts-receivable" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><AccountsReceivable /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial/dre" element={<ModuleGuard module="financial"><FinancialDRE /></ModuleGuard>} />
          <Route path="/admin/financial/rds" element={<ModuleGuard module="financial"><RDS /></ModuleGuard>} />
          <Route path="/admin/financial/cash-flow" element={<ModuleGuard module="financial"><CashFlow /></ModuleGuard>} />
          <Route path="/admin/financial/accounts" element={<ModuleGuard module="financial"><FinancialAccounts /></ModuleGuard>} />
          <Route path="/admin/financial/balance-sheet" element={<ModuleGuard module="financial"><BalanceSheet /></ModuleGuard>} />
          <Route path="/admin/financial/reports" element={<ModuleGuard module="financial"><FinancialReports /></ModuleGuard>} />
          <Route path="/admin/financial/sales-report" element={<ModuleGuard module="financial"><SalesReport /></ModuleGuard>} />
          <Route path="/admin/financial/commissions" element={<ModuleGuard module="financial"><Commissions /></ModuleGuard>} />
          <Route path="/admin/financial/stock-report" element={<ModuleGuard module="financial"><PermissionGuard permission="view_reports"><StockMovementReport /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/payroll/employees" element={<ModuleGuard module="financial"><PayrollEmployees /></ModuleGuard>} />
          <Route path="/admin/payroll" element={<ModuleGuard module="financial"><Payroll /></ModuleGuard>} />
          <Route path="/admin/recurring-payments" element={<ModuleGuard module="financial"><RecurringPayments /></ModuleGuard>} />
          <Route path="/admin/financial-projection" element={<ModuleGuard module="financial"><FinancialProjection /></ModuleGuard>} />
          <Route path="/admin/financial-notifications" element={<ModuleGuard module="financial"><FinancialNotifications /></ModuleGuard>} />
          <Route path="/admin/budget-goals" element={<ModuleGuard module="financial"><BudgetGoals /></ModuleGuard>} />
          <Route path="/admin/cost-centers" element={<ModuleGuard module="financial"><CostCenters /></ModuleGuard>} />
          <Route path="/admin/tax-reports" element={<ModuleGuard module="financial"><TaxReports /></ModuleGuard>} />
          <Route path="/admin/delinquency" element={<ModuleGuard module="financial"><Delinquency /></ModuleGuard>} />
          <Route path="/admin/audit-logs" element={<ModuleGuard module="financial"><AuditLogs /></ModuleGuard>} />
          <Route path="/admin/advanced-dashboards" element={<ModuleGuard module="financial"><AdvancedDashboards /></ModuleGuard>} />
          <Route path="/admin/sales" element={<ModuleGuard module="sales"><PermissionGuard permission="view_sales"><AdminSales /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/vendas" element={<ModuleGuard module="sales"><PermissionGuard permission="view_sales"><AdminVendas /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/fiscal" element={<ModuleGuard module="fiscal"><PermissionGuard permission="view_fiscal"><AdminFiscal /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/nfce-xmls" element={<ModuleGuard module="fiscal"><PermissionGuard permission="view_fiscal"><NFCeXMLs /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/relatorios" element={<ModuleGuard module="relatorios"><PermissionGuard permission="view_reports"><AdminRelatorios /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/webhook-monitor" element={<PermissionGuard permission="view_reports"><WebhookMonitor /></PermissionGuard>} />
          <Route path="/admin/settings" element={<PermissionGuard permission="view_settings"><AdminSettings /></PermissionGuard>} />
          <Route path="/admin/devices" element={<PermissionGuard permission="manage_devices"><Devices /></PermissionGuard>} />
          <Route path="/device-selector" element={<DeviceSelector />} />
          <Route path="/admin/collaborators" element={<ModuleGuard module="collaborators"><PermissionGuard permission="view_collaborators"><AdminCollaborators /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/roles" element={<ModuleGuard module="roles"><PermissionGuard permission="manage_roles"><AdminRoles /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/totem-settings" element={<ModuleGuard module="totem"><PermissionGuard permission="manage_totem"><AdminTotemSettings /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/suppliers" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><Suppliers /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/expense-categories" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><ExpenseCategories /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/accounts-payable" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><AccountsPayable /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/financial-dashboard" element={<ModuleGuard module="financial"><PermissionGuard permission="view_financial"><FinancialDashboard /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/cash-register" element={<ModuleGuard module="cash_register"><PermissionGuard permission="view_cash_register"><AdminCashRegister /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/mercadopago-tef" element={<ModuleGuard module="financial"><PermissionGuard permission="manage_payments"><MercadoPagoTEF /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/kitchen-labels" element={<ModuleGuard module="kitchen_labels"><PermissionGuard permission="view_kitchen_labels"><KitchenLabels /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/print-agent" element={<PermissionGuard permission="manage_settings"><PrintAgent /></PermissionGuard>} />
          
          {/* 👥 Clientes */}
          <Route path="/admin/clientes" element={<PermissionGuard permission="view_clients"><ClientsPage /></PermissionGuard>} />
          
          {/* 🍳 Sistema de Cozinha */}
          <Route path="/admin/recipes" element={<ModuleGuard module="recipe_management"><PermissionGuard permission="manage_products"><RecipeManagement /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/producao" element={<ModuleGuard module="recipe_management"><PermissionGuard permission="manage_products"><Producao /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/kitchen" element={<ModuleGuard module="kds"><KitchenDisplay /></ModuleGuard>} />

          {/* ✨ Aria - Assistente Neural de IA */}
          <Route path="/admin/aria" element={<ModuleGuard module="aria_ai"><AriaAssistant /></ModuleGuard>} />

          {/* 🎉 Sorteio do Instagram */}
          <Route path="/admin/instagram-giveaway" element={<ModuleGuard module="instagram_giveaway"><InstagramGiveaway /></ModuleGuard>} />

          {/* 📷 Câmeras de Segurança */}
          <Route path="/admin/security" element={<ModuleGuard module="security_cameras"><SecurityCameras /></ModuleGuard>} />
          
          {/* 📄 Importação de NF-e */}
          <Route path="/admin/nfe-import" element={<ModuleGuard module="nfe_import"><PermissionGuard permission="view_financial"><NFeImportPage /></PermissionGuard></ModuleGuard>} />
          
          {/* 🚚 Delivery */}
          <Route path="/admin/delivery" element={<ModuleGuard module="delivery"><PermissionGuard permission="view_delivery"><AdminDelivery /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/comanda" element={<ModuleGuard module="comanda"><PermissionGuard permission="view_comanda"><Comanda /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/cardapio-qr" element={<ModuleGuard module="table_menu"><PermissionGuard permission="view_table_menu"><TableQRCodes /></PermissionGuard></ModuleGuard>} />
          <Route path="/admin/delivery/orders" element={<ModuleGuard module="delivery"><PermissionGuard permission="view_delivery"><AdminDeliveryOrders /></PermissionGuard></ModuleGuard>} />
          <Route path="/d/:slug" element={<PublicDelivery />} />
          <Route path="/m/:slug/:token" element={<TableMenu />} />
          <Route path="/cardapio/:slug" element={<TableMenu viewOnly />} />
          <Route path="/d/:slug/order/:orderId" element={<PublicDelivery />} />

          {/* Suporte */}
          <Route path="/admin/suporte" element={<Suporte />} />
          
          {/* 🏨 Sistema de Hotelaria */}
          <Route path="/hotel" element={<HotelModule />} />
          <Route path="/hotel/*" element={<HotelModule />} />
          
          {/* 🌐 Booking Engine - Página Pública */}
          <Route path="/reservar/:propertyId" element={<BookingEngine />} />
          <Route path="/booking/:propertyId" element={<BookingEngine />} />
        </Routes>
      </Suspense>
    </>
  );
}

// Error Boundary para capturar ChunkLoadError e recarregar automaticamente
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      return { hasError: true };
    }
    return { hasError: true };
  }
  componentDidCatch(error) {
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      const reloaded = sessionStorage.getItem('chunk_error_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_error_reload', '1');
        window.location.reload();
      }
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f9fafb'}}>
          <div style={{textAlign:'center',padding:'2rem'}}>
            <h2 style={{fontSize:'1.25rem',fontWeight:'bold',marginBottom:'0.5rem'}}>Erro ao carregar</h2>
            <p style={{color:'#6b7280',marginBottom:'1rem'}}>Clique abaixo para recarregar</p>
            <button onClick={() => { sessionStorage.removeItem('chunk_error_reload'); window.location.reload(); }}
              style={{padding:'0.5rem 1.5rem',background:'#2563eb',color:'white',borderRadius:'0.5rem',border:'none',cursor:'pointer'}}>
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <div className="App">
      <DisableInspect />
      <BrowserRouter>
        <SubscriptionProvider>
          <ChunkErrorBoundary>
            <AppContent />
          </ChunkErrorBoundary>
        </SubscriptionProvider>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
